/**
 * IFC Exporter - Converts domain objects to IFC 4.3 format
 * Generates IFC files in STEP-21 format (ISO-10303-21)
 *
 * Key design points:
 * - IFC geometry is authored in meters (Project units are METRE).
 * - Optional georeferencing (IfcProjectedCRS + IfcMapConversion + Site placement) is computed in Domain (GeoReference)
 *   and serialized in the IFC project structure writer.
 * - This file focuses on orchestration: header + project structure + elements.
 */

import type { IModelConnection } from "@itwin/core-frontend";

import { Lighting } from "../../../domain/elements/Lighting";
import { Conduit } from "../../../domain/elements/Conduit";
import type { GeoReference } from "../../../domain/geospatial/GeoReference";
import { buildGeoReference } from "../../../domain/geospatial/GeoReference"; // <-- adjust path

import { generateHeader } from "./core/header";
import { generateProjectStructure } from "./project/projectStructure";

import { toIfcGuid } from "./core/guid";
import { createLocalPlacementAtOrigin } from "./core/placement";

import { writeDotPayItemPsets } from "./psets/dotPayItemPset";
import { writeFeaturePset } from "./psets/featurePset";
import { writeDrainageUtilitiesPset } from "./psets/drainageUtilitiesPset";
import { createMarkerCylinder } from "./core/marker";
import { ifcText, writePropertySetForElement, type IfcProp } from "./core/step";

//Meshes
import { createBodyTessellationFromMeshes } from "./core/tessellation";
import { createCurveRepresentationFromPrimitives } from "./core/curveRepresentation";
import type { IfcCurvePrimitive } from "../../../domain/utilities/CurvePrimitives";

const IFC_BLOB_MIME = "application/x-step";

/**
 * Generates a unique IFC entity ID (STEP #<n>=...)
 * NOTE: This is module-level state; resetEntityCounter() must be called at the start of each export.
 */
let entityCounter = 1;
let usedElementGuids = new Set<string>();
function getNextEntityId(): number {
  return entityCounter++;
}

/**
 * Resets the entity counter (call at start of each export)
 */
export function resetEntityCounter(): void {
  entityCounter = 1;
  usedElementGuids = new Set<string>();
}

function allocateUniqueIfcGuid(preferred?: string): string {
  let candidate = toIfcGuid(preferred);
  if (!usedElementGuids.has(candidate)) {
    usedElementGuids.add(candidate);
    return candidate;
  }

  do {
    candidate = toIfcGuid();
  } while (usedElementGuids.has(candidate));

  usedElementGuids.add(candidate);
  return candidate;
}

/**
 * Escapes special characters in IFC strings:
 * - single quotes are doubled:  '  => ''
 * - backslashes are doubled:    \  => \\
 */
function escapeIfcString(str: string): string {
  return str.replace(/'/g, "''").replace(/\\/g, "\\\\");
}

/**
 * Formats a value for IFC output:
 * - null/undefined => $
 * - string => '...'
 * - number => raw numeric text
 * - boolean => .T. / .F.
 * - Date => ISO string in quotes
 */
function formatIfcValue(value: unknown): string {
  if (value === null || value === undefined) return "$";
  if (typeof value === "string") return `'${escapeIfcString(value)}'`;
  if (typeof value === "number") return Number.isFinite(value) ? value.toString() : "$";
  if (typeof value === "boolean") return value ? ".T." : ".F.";
  if (value instanceof Date) return `'${value.toISOString()}'`;
  return `'${escapeIfcString(String(value))}'`;
}

type Point3 = [number, number, number];

function isNearZeroOrigin(origin: { x?: number; y?: number; z?: number } | undefined): boolean {
  if (!origin) return true;
  return (
    Math.abs(Number(origin.x ?? 0)) < 1e-9 &&
    Math.abs(Number(origin.y ?? 0)) < 1e-9 &&
    Math.abs(Number(origin.z ?? 0)) < 1e-9
  );
}

function pointMagnitude(p: Point3): number {
  return Math.hypot(p[0], p[1], p[2]);
}

function subtractPoint(p: Point3, offset: Point3): Point3 {
  return [p[0] - offset[0], p[1] - offset[1], p[2] - offset[2]];
}

function firstCurvePoint(curves: IfcCurvePrimitive[] | undefined): Point3 | null {
  if (!curves?.length) return null;
  for (const curve of curves) {
    if (curve.kind === "line") return [...curve.start];
    if (curve.kind === "polyline" && curve.points.length) return [...curve.points[0]];
    if (curve.kind === "arc") return [...curve.center];
  }
  return null;
}

function translateCurves(curves: IfcCurvePrimitive[], offset: Point3): IfcCurvePrimitive[] {
  return curves.map((curve) => {
    if (curve.kind === "line") {
      return {
        ...curve,
        start: subtractPoint(curve.start, offset),
        end: subtractPoint(curve.end, offset),
      };
    }

    if (curve.kind === "polyline") {
      return {
        ...curve,
        points: curve.points.map((p) => subtractPoint(p, offset)),
      };
    }

    return {
      ...curve,
      center: subtractPoint(curve.center, offset),
      start: subtractPoint(curve.start, offset),
      end: subtractPoint(curve.end, offset),
      vector0: [...curve.vector0],
      vector90: [...curve.vector90],
    };
  });
}

function writeNiceConnectExportPset(args: {
  ownerHistoryId: number;
  elementId: number;
  sourceElementId?: unknown;
  sourceRelatedElementIds?: unknown;
}): string {
  const props: IfcProp[] = [];
  const sourceElementId = String(args.sourceElementId ?? "").trim();
  const sourceRelatedElementIds = String(args.sourceRelatedElementIds ?? "").trim();

  if (sourceElementId) {
    props.push({ name: "Source Element Id", value: ifcText(sourceElementId) });
  }
  if (sourceRelatedElementIds) {
    props.push({
      name: "Source Related Element Ids",
      value: ifcText(sourceRelatedElementIds),
    });
  }

  if (props.length === 0) return "";

  return writePropertySetForElement({
    getNextId: getNextEntityId,
    ownerHistoryId: args.ownerHistoryId,
    elementId: args.elementId,
    psetName: "NiceConnect Export",
    props,
  });
}

/**
 * Converts a Lighting object to IFC entities
 *
 * Current approach:
 * - Uses IfcBuildingElementProxy (generic container) for quick interoperability.
 * - Uses a simple marker cylinder representation for debugging/visibility.
 * - Attaches property sets:
 *    - [Generic].[PhysicalObject]
 *    - DOT Pay Items
 *    - Feature
 * - Places the element under the Building spatial structure.
 */
function lightingToIfc(
  lighting: Lighting,
  ownerHistoryId: number,
  buildingId: number,
  buildingPlacementId: number,
  geometricContextId: number // kept for future geometry reps; used for marker geometry
): string {
  let output = "";
  const lightingCurves = (lighting as any).Curves as IfcCurvePrimitive[] | undefined;
  let placementOrigin = lighting.Origin;
  let curvesForExport = lightingCurves;

  // Some 2D cells carry absolute/world coordinates in Curves while Origin is zero.
  // Localize those curves so ObjectPlacement is no longer stuck at model origin.
  if (lightingCurves?.length && isNearZeroOrigin(lighting.Origin)) {
    const anchor = firstCurvePoint(lightingCurves);
    if (anchor && pointMagnitude(anchor) > 1) {
      placementOrigin = { x: anchor[0], y: anchor[1], z: anchor[2] } as any;
      curvesForExport = translateCurves(lightingCurves, anchor);
    }
  }

  /**
   * Helper: allocate an entity id and write a full STEP line:
   *   #<id>=<rhs>\n
   * Returns the allocated id for later reference.
   */
  const addEntityLine = (rhs: string): number => {
    const id = getNextEntityId();
    output += `#${id}=${rhs}\n`;
    return id;
  };

  // Allocate the element id (IfcBuildingElementProxy)
  const elementId = getNextEntityId();

  // -----------------------------------
  // Placement (IfcLocalPlacement)
  // -----------------------------------
  // Uses Lighting.Origin (meters) and places relative to the building placement.
  const placement = createLocalPlacementAtOrigin(
    getNextEntityId,
    placementOrigin,
    buildingPlacementId
  );

  output += placement.output;
  const localPlacementId = placement.localPlacementId;

  // -----------------------------------
  // Representation (debug marker)
  // -----------------------------------
  // Marker cylinder is strictly for visibility/testing. You can remove later.
  // const marker = createMarkerCylinder({
  //   getNextId: getNextEntityId,
  //   geometricContextId,
  //   radiusM: 0.20,
  //   heightM: 0.20,
  // });

  // output += marker.output;
  // const representationId = marker.productShapeId;

  // -----------------------------------
  // Representation (Meshes)
  // -----------------------------------
  let representationId: number;
  if (curvesForExport?.length) {
    const curveRep = createCurveRepresentationFromPrimitives({
      getNextId: getNextEntityId,
      geometricContextId,
      curves: curvesForExport,
    });
    output += curveRep.output;
    representationId = curveRep.productShapeId!;
  } else if ((lighting as any).Meshes?.length) {
    const tess = createBodyTessellationFromMeshes({
      getNextId: getNextEntityId,
      geometricContextId,
      meshes: (lighting as any).Meshes,
    });
    output += tess.output;
    representationId = tess.productShapeId!;
  } else {
    const marker = createMarkerCylinder({
      getNextId: getNextEntityId,
      geometricContextId,
      radiusM: 0.20,
      heightM: 0.20,
    });
    output += marker.output;
    representationId = marker.productShapeId;
  }

  // -----------------------------------
  // Element entity (IfcBuildingElementProxy)
  // -----------------------------------
  const guid = allocateUniqueIfcGuid(lighting.FederationGUID);
  (lighting as any).__ifcGlobalId = guid;

  // Note: IfcBuildingElementProxy signature (IFC4x3) includes:
  // GlobalId, OwnerHistory, Name, Description, ObjectType, ObjectPlacement, Representation, Tag
  output += `#${elementId}=IFCBUILDINGELEMENTPROXY(
    '${guid}',
    #${ownerHistoryId},
    ${formatIfcValue(lighting.UserLabel)},
    '[Generic].[PhysicalObject]',
    ${formatIfcValue(lighting.Model ?? "")},
    #${localPlacementId},
    #${representationId},
    $
  );\n`;

  // -----------------------------------
  // Property Set: [Generic].[PhysicalObject]
  // -----------------------------------
  const genericPropsId = getNextEntityId();
  const genericPropIds: number[] = [];

  // Basic "In Spatial Index" flag (matches many Bentley exports)
  genericPropIds.push(
    addEntityLine(`IFCPROPERTYSINGLEVALUE('In Spatial Index',$,IFCBOOLEAN(.T.),$);`)
  );

  // Optional Last Modified
  if (lighting.LastMod) {
    genericPropIds.push(
      addEntityLine(
        `IFCPROPERTYSINGLEVALUE('Last Modified',$,IFCDATETIME('${lighting.LastMod.toISOString()}'),$);`
      )
    );
  }

  // Federation GUID
  genericPropIds.push(
    addEntityLine(
      `IFCPROPERTYSINGLEVALUE('Federation GUID',$,IFCTEXT(${formatIfcValue(guid)}),$);`
    )
  );

  // User label (if present)
  if (lighting.UserLabel) {
    genericPropIds.push(
      addEntityLine(
        `IFCPROPERTYSINGLEVALUE('User Label',$,IFCTEXT(${formatIfcValue(lighting.UserLabel)}),$);`
      )
    );
  }

  // Write the property set entity itself
  output += `#${genericPropsId}=IFCPROPERTYSET('${toIfcGuid()}',#${ownerHistoryId},'[Generic].[PhysicalObject]',$,(#${genericPropIds.join(
    ",#"
  )}));\n`;

  // Relate pset to element
  const relGenericId = getNextEntityId();
  output += `#${relGenericId}=IFCRELDEFINESBYPROPERTIES('${toIfcGuid()}',#${ownerHistoryId},$,$,(#${elementId}),#${genericPropsId});\n`;
  output += writeNiceConnectExportPset({
    ownerHistoryId,
    elementId,
    sourceElementId: (lighting as any).__sourceElementId,
    sourceRelatedElementIds: (lighting as any).__sourceRelatedElementIds,
  });

  // -----------------------------------
  // DOT Pay Items (Psets)
  // -----------------------------------
  output += writeDotPayItemPsets({
    getNextId: getNextEntityId,
    ownerHistoryId,
    elementId,
    dotPayItem1: lighting.DOTPayItem1,
    dotPayItem2: lighting.DOTPayItem2,
    dotPayItems: lighting.DOTPayItems,
  });

  // -----------------------------------
  // Drainage Utilities (Pset)
  // -----------------------------------
  output += writeDrainageUtilitiesPset({
    getNextId: getNextEntityId,
    ownerHistoryId,
    elementId,
    drainageUtilities: lighting.DrainageUtilities,
    utilityData: lighting.UtilityData,
    utilityFootprint: lighting.UtilityFootprint,
    utilityQuality: lighting.UtilityQuality,
  });

  // -----------------------------------
  // Feature Pset
  // -----------------------------------
  output += writeFeaturePset({
    getNextId: getNextEntityId,
    ownerHistoryId,
    elementId,
    feature: lighting.Feature,
    featureDefinitionOverride: (lighting as any).__featureLabel,
  });

  // -----------------------------------
  // (Optional) Property Set: Feature (placeholder)
  // -----------------------------------
  // NOTE: Your current code allocates "featurePropsIds" but never adds any props.
  // Keeping this block as a scaffold for future Feature properties.
  if (lighting.Feature) {
    const featurePropsId = getNextEntityId();
    const featurePropsIds: number[] = [];

    // Example: if you later add feature props, push ids into featurePropsIds.

    if (featurePropsIds.length > 0) {
      output += `#${featurePropsId}=IFCPROPERTYSET('${toIfcGuid()}',#${ownerHistoryId},'Feature',$,(#${featurePropsIds.join(
        ",#"
      )}));\n`;

      const relFeatureId = getNextEntityId();
      output += `#${relFeatureId}=IFCRELDEFINESBYPROPERTIES('${toIfcGuid()}',#${ownerHistoryId},$,$,(#${elementId}),#${featurePropsId});\n`;
    }
  }

  // -----------------------------------
  // Containment: put element into the building
  // -----------------------------------
  const relContainedId = getNextEntityId();
  output += `#${relContainedId}=IFCRELCONTAINEDINSPATIALSTRUCTURE('${toIfcGuid()}',#${ownerHistoryId},$,$,(#${elementId}),#${buildingId});\n`;

  return output;
}

/**
 * Converts a Conduit object to IFC entities
 * - Uses IfcCableSegment for conduit.
 * - Attaches property sets:
 *    - [Generic].[PhysicalObject]
 *    - DOT Pay Items
 *    - Feature
 */
function conduitToIfc(
  conduit: Conduit,
  ownerHistoryId: number,
  buildingId: number,
  buildingPlacementId: number,
  geometricContextId: number
): string {
  let output = "";

  const addEntityLine = (rhs: string): number => {
    const id = getNextEntityId();
    output += `#${id}=${rhs}\n`;
    return id;
  };

  const elementId = getNextEntityId();

  const placement = createLocalPlacementAtOrigin(
    getNextEntityId,
    conduit.Origin,
    buildingPlacementId
  );

  output += placement.output;
  const localPlacementId = placement.localPlacementId;

  const conduitCurves = (conduit as any).Curves as IfcCurvePrimitive[] | undefined;
  const conduitMeshes = (conduit as any).Meshes;
  let representationId: number;
  if (conduitCurves?.length && conduitMeshes?.length) {
    const tess = createBodyTessellationFromMeshes({
      getNextId: getNextEntityId,
      geometricContextId,
      meshes: conduitMeshes,
      wrapInProductShape: false,
    });
    output += tess.output;

    const curveRep = createCurveRepresentationFromPrimitives({
      getNextId: getNextEntityId,
      geometricContextId,
      curves: conduitCurves,
      wrapInProductShape: false,
    });
    output += curveRep.output;

    representationId = getNextEntityId();
    output += `#${representationId}=IFCPRODUCTDEFINITIONSHAPE($,$,(#${tess.shapeRepId},#${curveRep.shapeRepId}));\n`;
  } else if (conduitCurves?.length) {
    const curveRep = createCurveRepresentationFromPrimitives({
      getNextId: getNextEntityId,
      geometricContextId,
      curves: conduitCurves,
    });
    output += curveRep.output;
    representationId = curveRep.productShapeId!;
  } else if (conduitMeshes?.length) {
    const tess = createBodyTessellationFromMeshes({
      getNextId: getNextEntityId,
      geometricContextId,
      meshes: conduitMeshes,
    });
    output += tess.output;
    representationId = tess.productShapeId!;
  } else {
    const marker = createMarkerCylinder({
      getNextId: getNextEntityId,
      geometricContextId,
      radiusM: 0.20,
      heightM: 0.20,
    });
    output += marker.output;
    representationId = marker.productShapeId;
  }

  const guid = allocateUniqueIfcGuid(conduit.FederationGUID);
  (conduit as any).__ifcGlobalId = guid;

  // IfcCableSegment signature (IFC4x3) includes:
  // GlobalId, OwnerHistory, Name, Description, ObjectType, ObjectPlacement, Representation, Tag, PredefinedType
  output += `#${elementId}=IFCCABLESEGMENT(
    '${guid}',
    #${ownerHistoryId},
    ${formatIfcValue(conduit.UserLabel)},
    'Conduit',
    ${formatIfcValue(conduit.Model ?? "")},
    #${localPlacementId},
    #${representationId},
    $,
    .NOTDEFINED.
  );\n`;

  const genericPropsId = getNextEntityId();
  const genericPropIds: number[] = [];

  genericPropIds.push(
    addEntityLine(`IFCPROPERTYSINGLEVALUE('In Spatial Index',$,IFCBOOLEAN(.T.),$);`)
  );

  if (conduit.LastMod) {
    genericPropIds.push(
      addEntityLine(
        `IFCPROPERTYSINGLEVALUE('Last Modified',$,IFCDATETIME('${conduit.LastMod.toISOString()}'),$);`
      )
    );
  }

  genericPropIds.push(
    addEntityLine(
      `IFCPROPERTYSINGLEVALUE('Federation GUID',$,IFCTEXT(${formatIfcValue(guid)}),$);`
    )
  );

  if (conduit.UserLabel) {
    genericPropIds.push(
      addEntityLine(
        `IFCPROPERTYSINGLEVALUE('User Label',$,IFCTEXT(${formatIfcValue(conduit.UserLabel)}),$);`
      )
    );
  }

  output += `#${genericPropsId}=IFCPROPERTYSET('${toIfcGuid()}',#${ownerHistoryId},'[Generic].[PhysicalObject]',$,(#${genericPropIds.join(
    ",#"
  )}));\n`;

  const relGenericId = getNextEntityId();
  output += `#${relGenericId}=IFCRELDEFINESBYPROPERTIES('${toIfcGuid()}',#${ownerHistoryId},$,$,(#${elementId}),#${genericPropsId});\n`;
  output += writeNiceConnectExportPset({
    ownerHistoryId,
    elementId,
    sourceElementId: (conduit as any).__sourceElementId,
    sourceRelatedElementIds: (conduit as any).__sourceRelatedElementIds,
  });

  output += writeDotPayItemPsets({
    getNextId: getNextEntityId,
    ownerHistoryId,
    elementId,
    dotPayItem1: conduit.DOTPayItem1,
    dotPayItem2: conduit.DOTPayItem2,
    dotPayItems: conduit.DOTPayItems,
  });

  output += writeDrainageUtilitiesPset({
    getNextId: getNextEntityId,
    ownerHistoryId,
    elementId,
    drainageUtilities: conduit.DrainageUtilities,
    utilityData: conduit.UtilityData,
    utilityFootprint: conduit.UtilityFootprint,
    utilityQuality: conduit.UtilityQuality,
  });

  output += writeFeaturePset({
    getNextId: getNextEntityId,
    ownerHistoryId,
    elementId,
    feature: conduit.Feature,
    featureDefinitionOverride: (conduit as any).__featureLabel,
  });

  if (conduit.Feature) {
    const featurePropsId = getNextEntityId();
    const featurePropsIds: number[] = [];

    if (featurePropsIds.length > 0) {
      output += `#${featurePropsId}=IFCPROPERTYSET('${toIfcGuid()}',#${ownerHistoryId},'Feature',$,(#${featurePropsIds.join(
        ",#"
      )}));\n`;

      const relFeatureId = getNextEntityId();
      output += `#${relFeatureId}=IFCRELDEFINESBYPROPERTIES('${toIfcGuid()}',#${ownerHistoryId},$,$,(#${elementId}),#${featurePropsId});\n`;
    }
  }

  const relContainedId = getNextEntityId();
  output += `#${relContainedId}=IFCRELCONTAINEDINSPATIALSTRUCTURE('${toIfcGuid()}',#${ownerHistoryId},$,$,(#${elementId}),#${buildingId});\n`;

  return output;
}

/**
 * Synchronous export:
 * - Useful for tests or contexts where GeoReference has been computed already (or is not needed).
 * - If you pass "geo", projectStructure will emit IfcProjectedCRS/IfcMapConversion/Site placement + lat/long.
 * - If you omit "geo", export still produces valid IFC but not georeferenced.
 */
export function exportLightingsToIfc(
  lightings: Lighting[],
  geo?: GeoReference
): string {
  resetEntityCounter();

  // 1) HEADER section
  let output = generateHeader();

  // 2) DATA section: Project / Site / Building (+ optional georeferencing)
  const project = generateProjectStructure(getNextEntityId, geo);
  output += project.output;

  // 3) Elements
  for (const lighting of lightings) {
    output += lightingToIfc(
      lighting,
      project.ownerHistoryId,
      project.buildingId,
      project.buildingPlacementId,
      project.geometricContextId
    );
  }

  // 4) Close STEP
  output += "ENDSEC;\n";
  output += "END-ISO-10303-21;\n";

  return output;
}

/**
 * Export both lightings and conduits into a single IFC.
 */
export function exportElectricalToIfc(
  lightings: Lighting[],
  conduits: Conduit[],
  geo?: GeoReference
): string {
  resetEntityCounter();

  let output = generateHeader();
  const project = generateProjectStructure(getNextEntityId, geo);
  output += project.output;

  for (const lighting of lightings) {
    output += lightingToIfc(
      lighting,
      project.ownerHistoryId,
      project.buildingId,
      project.buildingPlacementId,
      project.geometricContextId
    );
  }

  for (const conduit of conduits) {
    output += conduitToIfc(
      conduit,
      project.ownerHistoryId,
      project.buildingId,
      project.buildingPlacementId,
      project.geometricContextId
    );
  }

  output += "ENDSEC;\n";
  output += "END-ISO-10303-21;\n";

  return output;
}

/**
 * Async export:
 * - Computes GeoReference from an iModel, then calls the core exporter.
 * - This is the preferred path for producing georeferenced IFC.
 *
 * NOTE:
 * - buildGeoReference uses geoServices conversion where available.
 * - If conversion is not available in your runtime, buildGeoReference should return siteRef-only (ECEF)
 *   or you should compute georeference via backend RPC.
 */
export async function exportLightingsToIfcFromIModel(
  iModel: IModelConnection,
  lightings: Lighting[]
): Promise<string> {
  // Compute georeference in Domain (optional)
  const geo = await buildGeoReference(iModel);

  // Delegate to the synchronous exporter (keeps one canonical writing path)
  return exportLightingsToIfc(lightings, geo);
}

export async function exportElectricalToIfcFromIModel(
  iModel: IModelConnection,
  lightings: Lighting[],
  conduits: Conduit[]
): Promise<string> {
  const geo = await buildGeoReference(iModel);
  return exportElectricalToIfc(lightings, conduits, geo);
}

/**
 * Blob export (sync)
 * - Use when you already have (or don't need) georeferencing.
 */
export function exportLightingsToIfcBlob(
  lightings: Lighting[],
  filename?: string,
  geo?: GeoReference
): Blob {
  const ifcContent = exportLightingsToIfc(lightings, geo);
  return new Blob([ifcContent], { type: IFC_BLOB_MIME });
}

export function exportElectricalToIfcBlob(
  lightings: Lighting[],
  conduits: Conduit[],
  filename?: string,
  geo?: GeoReference
): Blob {
  const ifcContent = exportElectricalToIfc(lightings, conduits, geo);
  return new Blob([ifcContent], { type: IFC_BLOB_MIME });
}

/**
 * Blob export (async from iModel)
 * - Preferred for UI download flows where iModel is available.
 */
export async function exportLightingsToIfcBlobFromIModel(
  iModel: IModelConnection,
  lightings: Lighting[],
  filename?: string
): Promise<Blob> {
  const ifcContent = await exportLightingsToIfcFromIModel(iModel, lightings);
  return new Blob([ifcContent], { type: IFC_BLOB_MIME });
}

export async function exportElectricalToIfcBlobFromIModel(
  iModel: IModelConnection,
  lightings: Lighting[],
  conduits: Conduit[],
  filename?: string
): Promise<Blob> {
  const ifcContent = await exportElectricalToIfcFromIModel(iModel, lightings, conduits);
  return new Blob([ifcContent], { type: IFC_BLOB_MIME });
}
